<template>
    <div class="start-grid-print-page">
        <section class="print-controls">
            <div class="controls-inner">
                <div class="control-group">
                    <label class="control-label">{{ $t("startGrids.print_sheet_type") }}</label>
                    <div class="segmented-control">
                        <button
                            type="button"
                            :class="{ active: printMode === 'announcer' }"
                            @click="printMode = 'announcer'"
                        >
                            {{ $t("startGrids.print_announcer_sheet") }}
                        </button>
                        <button
                            type="button"
                            :class="{ active: printMode === 'raceOrderWithName' }"
                            @click="printMode = 'raceOrderWithName'"
                        >
                            {{ $t("startGrids.print_race_order_with_name") }}
                        </button>
                        <button
                            type="button"
                            :class="{ active: printMode === 'raceOrderCompact' }"
                            @click="printMode = 'raceOrderCompact'"
                        >
                            {{ $t("startGrids.print_race_order_compact") }}
                        </button>
                    </div>
                </div>

                <div class="control-group staging-format-control" v-if="isStagingMode">
                    <label class="control-label">{{ $t("startGrids.print_staging_format") }}</label>
                    <div class="segmented-control">
                        <button
                            type="button"
                            :class="{ active: stagingPrintFormat === 'regular' }"
                            @click="stagingPrintFormat = 'regular'"
                        >
                            {{ $t("startGrids.print_format_regular") }}
                        </button>
                        <button
                            type="button"
                            :class="{ active: stagingPrintFormat === 'compact' }"
                            @click="stagingPrintFormat = 'compact'"
                        >
                            {{ $t("startGrids.print_format_compact") }}
                        </button>
                    </div>
                </div>

                <div class="controls-row">
                    <div class="control-group">
                        <label class="control-label">{{ $t("startGrids.print_scope") }}</label>
                        <div class="radio-row">
                            <label class="radio-option">
                                <input type="radio" value="all" v-model="printScope">
                                <span>{{ $t("startGrids.print_all_items") }}</span>
                            </label>
                            <label class="radio-option">
                                <input type="radio" value="selected" v-model="printScope">
                                <span>{{ $t("startGrids.print_selected_items") }}</span>
                            </label>
                        </div>
                    </div>

                    <div class="actions">
                        <span class="selection-count">
                            {{ $t("startGrids.print_selected_count", { count: displayItems.length }) }}
                        </span>
                        <button type="button" @click="selectAllItems">
                            {{ $t("startGrids.print_select_all") }}
                        </button>
                        <button type="button" @click="clearSelection">
                            {{ $t("startGrids.print_clear_selection") }}
                        </button>
                        <button type="button" class="primary-action" :disabled="!hasPrintableItems" @click="printSheet">
                            {{ $t("startGrids.print") }}
                        </button>
                    </div>
                </div>

                <div class="item-selector" v-if="printScope === 'selected'">
                    <label
                        v-for="item in printItems"
                        :key="item.key"
                        class="item-option"
                    >
                        <input type="checkbox" :value="item.key" v-model="selectedItemKeys">
                        <span>{{ itemTitle(item) }}</span>
                    </label>
                </div>
            </div>
        </section>

        <div id="print" class="print-container" :class="printContainerClasses">
            <header class="print-header" v-if="headerData && headerData.eventName">
                <div class="event-name">{{ headerData.eventName }} - {{ headerData.eventStartDate | formatDateForHumans(false) }}</div>
                <div class="start-grid-name">{{ headerData.startGridName }}</div>
            </header>

            <div class="empty-print-message" v-if="!hasPrintableItems">
                {{ $t("startGrids.print_no_items") }}
            </div>

            <table v-else-if="printMode === 'raceOrderCompact'" class="race-order-table">
                <thead>
                    <tr>
                        <th>{{ $t("startGrids.race-number") }}</th>
                        <th>{{ $t("startGrids.class_name") }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in displayItems" :key="`compact-${item.key}`">
                        <td>{{ displayRaceNumber(item) }}</td>
                        <td>
                            <span
                                v-for="(className, classIndex) in classNameLines(item.classNames.length ? item.classNames : item.className)"
                                :key="`${item.key}-class-${classIndex}`"
                                class="class-name-line"
                            >{{ className }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div v-else class="sheet-grid">
                <section v-for="item in displayItems" :key="`item-${item.key}`" class="class-section">
                    <div class="class-name">
                        {{ itemTitle(item) }}
                    </div>

                    <table v-if="printMode === 'announcer'" class="participants-table announcer-table">
                        <thead>
                            <tr>
                                <th>{{ $t("startGrids.start-row") }}</th>
                                <th>{{ $t("startGrids.position") }}</th>
                                <th>{{ $t("startGrids.class_name") }}</th>
                                <th>{{ $t("startGrids.race-number") }}</th>
                                <th>{{ $t("startGrids.first-name") }}</th>
                                <th>{{ $t("startGrids.last-name") }}</th>
                                <th>{{ $t("startGrids.age") }}</th>
                                <th>{{ $t("startGrids.state") }}</th>
                                <th>{{ $t("startGrids.brand") }}</th>
                                <th>{{ $t("startGrids.sponsors") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(participant, index) in announcerParticipants(item)" :key="`announcer-${item.key}-${index}`">
                                <td>{{ participant.startRow }}</td>
                                <td>{{ participant.position }}</td>
                                <td>{{ participant.className }}</td>
                                <td>{{ participant.number }}</td>
                                <td>{{ participant.firstname }}</td>
                                <td>{{ participant.lastname }}</td>
                                <td>{{ participant.age }}</td>
                                <td>{{ participant.state }}</td>
                                <td>{{ participant.brand }}</td>
                                <td>{{ participant.sponsors }}</td>
                            </tr>
                        </tbody>
                    </table>

                    <table v-else class="participants-table race-order-name-table">
                        <thead>
                            <tr>
                                <th>{{ stagingColumnLabel('start-row', 'print_compact_start_row') }}</th>
                                <th>{{ stagingColumnLabel('position', 'print_compact_position') }}</th>
                                <th>{{ stagingColumnLabel('class_name', 'print_compact_class') }}</th>
                                <th>{{ stagingColumnLabel('race-number', 'print_compact_race_number') }}</th>
                                <th>{{ $t("startGrids.rider_name") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(participant, index) in item.startGrids" :key="`race-order-name-${item.key}-${index}`">
                                <td>{{ participant.startRow }}</td>
                                <td>{{ participant.position }}</td>
                                <td>{{ participant.className }}</td>
                                <td>{{ participant.number }}</td>
                                <td>{{ participantName(participant) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import { apiCalls } from "@/mixins/apiCalls.js";

export default {
    mixins: [apiCalls],
    data() {
        return {
            items: [],
            startGridId: 0,
            classesId: null,
            raceId: null,
            headerData: {},
            printMode: 'announcer',
            stagingPrintFormat: 'regular',
            printScope: 'all',
            selectedItemKeys: []
        };
    },
    computed: {
        printItems() {
            const flattened = [];
            this.items.forEach((item) => {
                const classStartGrids = item.classStartGrids || [];
                classStartGrids.forEach((grid, index) => {
                    const raceNumber = this.normalizeRaceNumber(grid.raceNumber);
                    flattened.push({
                        key: this.itemKey(item, grid, index),
                        raceId: String(item.raceId || ''),
                        raceName: item.raceName || '',
                        raceStartGridLinkId: item.raceStartGridLinkId || '',
                        classConfigId: grid.classConfigId || '',
                        classId: String(grid.classId || ''),
                        classIds: this.gridClassIds(grid),
                        className: grid.className || '',
                        classNames: Array.isArray(grid.classNames) ? grid.classNames : [],
                        raceNumber,
                        sortRaceNumber: raceNumber === '' ? 999999999 : Number(raceNumber),
                        startGrids: this.sortedParticipants(grid.startGrids || []),
                        grid
                    });
                });
            });

            return flattened.sort((a, b) => {
                if (a.sortRaceNumber !== b.sortRaceNumber) {
                    return a.sortRaceNumber - b.sortRaceNumber;
                }

                return a.className.localeCompare(b.className);
            });
        },
        displayItems() {
            if (this.printScope === 'all') {
                return this.printItems;
            }

            return this.printItems.filter((item) => this.selectedItemKeys.indexOf(item.key) !== -1);
        },
        hasPrintableItems() {
            return this.displayItems.length > 0;
        },
        isStagingMode() {
            return this.printMode === 'raceOrderWithName';
        },
        isCompactStagingMode() {
            return this.isStagingMode && this.stagingPrintFormat === 'compact';
        },
        printContainerClasses() {
            const classes = [`print-${this.printMode}`];

            if (this.isStagingMode) {
                classes.push(`print-staging-${this.stagingPrintFormat}`);
            }

            return classes;
        }
    },
    created() {
        this.classesId = this.$route.query.classId;
        this.classesId = this.classesId ? String(this.classesId).split(",") : null;
        this.raceId = this.$route.query.raceId ? String(this.$route.query.raceId) : null;
        this.getData();
    },
    methods: {
        normalizeRaceNumber(value) {
            if (value === null || typeof value === 'undefined' || value === '') {
                return '';
            }

            const raceNumber = Number(value);
            if (isNaN(raceNumber) || raceNumber >= 2147483647) {
                return '';
            }

            return raceNumber;
        },
        displayRaceNumber(item) {
            return item.raceNumber === '' ? '' : item.raceNumber;
        },
        gridClassIds(grid) {
            if (Array.isArray(grid.classIds)) {
                return grid.classIds.map((classId) => String(classId));
            }

            if (Array.isArray(grid.classId)) {
                return grid.classId.map((classId) => String(classId));
            }

            return String(grid.classId || '').split(',').filter((classId) => classId !== '');
        },
        itemKey(item, grid, index) {
            return [
                item.raceId || '',
                item.raceStartGridLinkId || '',
                grid.classConfigId || '',
                grid.classId || '',
                index
            ].join(':');
        },
        sortedParticipants(participants) {
            return [...participants].sort((a, b) => {
                const rowA = Number(a.startRow || 0);
                const rowB = Number(b.startRow || 0);
                if (rowA !== rowB) {
                    return rowA - rowB;
                }

                return Number(a.position || 0) - Number(b.position || 0);
            });
        },
        announcerParticipants(item) {
            return this.sortedAnnouncerParticipants(item.startGrids || []);
        },
        sortedAnnouncerParticipants(participants) {
            return [...participants].sort((a, b) => {
                const numberA = this.participantRaceNumberSortValue(a.number);
                const numberB = this.participantRaceNumberSortValue(b.number);

                if (numberA === '' || numberB === '') {
                    if (numberA !== numberB) {
                        return numberA === '' ? 1 : -1;
                    }
                } else {
                    const textCompare = this.compareParticipantRaceNumbers(numberA, numberB);
                    if (textCompare !== 0) {
                        return textCompare;
                    }
                }

                const rowA = Number(a.startRow || 0);
                const rowB = Number(b.startRow || 0);
                if (rowA !== rowB) {
                    return rowA - rowB;
                }

                return Number(a.position || 0) - Number(b.position || 0);
            });
        },
        compareParticipantRaceNumbers(left, right) {
            const leftTokens = left.toUpperCase().match(/\d+|\D+/g) || [];
            const rightTokens = right.toUpperCase().match(/\d+|\D+/g) || [];
            const tokenCount = Math.min(leftTokens.length, rightTokens.length);

            for (let index = 0; index < tokenCount; index++) {
                const leftToken = leftTokens[index];
                const rightToken = rightTokens[index];
                const leftIsNumeric = /^\d+$/.test(leftToken);
                const rightIsNumeric = /^\d+$/.test(rightToken);

                if (leftIsNumeric && rightIsNumeric) {
                    const leftDigits = leftToken.replace(/^0+(?=\d)/, '');
                    const rightDigits = rightToken.replace(/^0+(?=\d)/, '');

                    if (leftDigits.length !== rightDigits.length) {
                        return leftDigits.length - rightDigits.length;
                    }
                    if (leftDigits !== rightDigits) {
                        return leftDigits < rightDigits ? -1 : 1;
                    }
                } else if (leftIsNumeric !== rightIsNumeric) {
                    return leftIsNumeric ? -1 : 1;
                } else if (leftToken !== rightToken) {
                    return leftToken < rightToken ? -1 : 1;
                }
            }

            return leftTokens.length - rightTokens.length;
        },
        participantRaceNumberSortValue(value) {
            return value === null || typeof value === 'undefined' ? '' : String(value).trim();
        },
        formatClassNames(className) {
            return String(className || '').replace(/,\s+/g, ', ');
        },
        /**
         * Keep one Race Order row while placing each combined class on its
         * own visual line. New responses provide an array so commas inside a
         * legitimate class name stay intact; string parsing remains only for
         * cached responses from servers deployed before that contract.
         */
        classNameLines(classNames) {
            const names = Array.isArray(classNames) ? classNames : String(classNames || '').split(',');

            return names
                .map((name) => String(name).trim())
                .filter((name) => name !== '');
        },
        itemTitle(item) {
            const raceLabel = this.displayRaceNumber(item) === '' ? 'Race' : `Race ${this.displayRaceNumber(item)}`;
            return `${raceLabel} (${item.raceName}) - ${this.formatClassNames(item.className)}`;
        },
        participantName(participant) {
            return [participant.firstname, participant.lastname].filter((value) => !!value).join(' ');
        },
        stagingColumnLabel(defaultKey, compactKey) {
            const key = this.isCompactStagingMode ? compactKey : defaultKey;
            return this.$t(`startGrids.${key}`);
        },
        matchesRouteFilter(item) {
            if (this.raceId && item.raceId !== this.raceId) {
                return false;
            }

            if (!this.classesId || this.classesId.length === 0) {
                return true;
            }

            return item.classIds.some((classId) => this.classesId.indexOf(classId) !== -1);
        },
        applyInitialSelection() {
            const routeFilteredKeys = this.printItems
                .filter((item) => this.matchesRouteFilter(item))
                .map((item) => item.key);

            if (this.raceId || this.classesId) {
                this.printScope = 'selected';
                this.selectedItemKeys = routeFilteredKeys;
                return;
            }

            this.selectAllItems();
        },
        selectAllItems() {
            this.selectedItemKeys = this.printItems.map((item) => item.key);
        },
        clearSelection() {
            this.selectedItemKeys = [];
            this.printScope = 'selected';
        },
        printSheet() {
            if (!this.hasPrintableItems) {
                return;
            }

            window.print();
        },
        async getData() {
            const response = await this.getApi(`/api/v1/promoter/startGrid/print/${this.$route.params.id}`);
            if(response && response.success) {
                this.startGridId = response.message.startGridId;
                this.headerData = response.message.headerData;
                this.items = response.message.data;
                this.$nextTick(() => {
                    this.applyInitialSelection();
                });
            }
        }
    }
};
</script>

<style scoped>
.start-grid-print-page {
    background: #f5f6f8;
    color: #111827;
    min-height: 100vh;
}

.print-controls {
    background: #ffffff;
    border-bottom: 1px solid #d8dde6;
    padding: 18px 24px;
}

.controls-inner {
    max-width: 1200px;
    margin: 0 auto;
}

.controls-row {
    align-items: flex-end;
    display: flex;
    gap: 24px;
    justify-content: space-between;
    margin-top: 16px;
}

.control-group {
    min-width: 0;
}

.staging-format-control {
    margin-top: 14px;
}

.control-label {
    color: #374151;
    display: block;
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 8px;
}

.segmented-control {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.segmented-control button,
.actions button {
    background: #ffffff;
    border: 1px solid #aeb8c8;
    color: #111827;
    cursor: pointer;
    font-size: 14px;
    font-weight: 700;
    min-height: 36px;
    padding: 7px 12px;
}

.segmented-control button.active,
.actions .primary-action {
    background: #1d4ed8;
    border-color: #1d4ed8;
    color: #ffffff;
}

.actions .primary-action:disabled {
    background: #9ca3af;
    border-color: #9ca3af;
    cursor: not-allowed;
}

.radio-row {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
}

.radio-option,
.item-option {
    align-items: center;
    display: inline-flex;
    gap: 7px;
}

.actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
}

.selection-count {
    color: #4b5563;
    font-size: 13px;
    font-weight: 700;
    margin-right: 4px;
}

.item-selector {
    border-top: 1px solid #e5e7eb;
    display: grid;
    gap: 8px 18px;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    margin-top: 16px;
    padding-top: 14px;
}

.item-option {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    font-size: 13px;
    min-height: 34px;
    padding: 7px 9px;
}

.print-container {
    background: #ffffff;
    margin: 18px auto;
    max-width: 1280px;
    min-height: calc(100vh - 180px);
    padding: 18px;
}

.print-header {
    border-bottom: 2px solid #111827;
    margin-bottom: 12px;
    padding-bottom: 8px;
    text-align: center;
}

.event-name {
    font-size: 20px;
    font-weight: 800;
}

.start-grid-name {
    font-size: 16px;
    font-weight: 700;
    margin-top: 3px;
}

.sheet-grid {
    column-count: 2;
    column-gap: 12px;
}

.print-staging-compact .sheet-grid {
    column-count: 3;
    column-fill: auto;
    column-gap: 0;
    display: block;
}

.class-section {
    break-inside: avoid;
    margin-bottom: 12px;
}

.print-staging-compact .class-section {
    border: 1px solid #000;
    box-sizing: border-box;
    break-inside: auto;
    margin-bottom: 15px;
    page-break-inside: auto;
}

.class-name {
    background-color: #f5f000 !important;
    border: 1px solid #bfbfbf;
    font-size: 13px;
    font-weight: 800;
    margin-bottom: 4px;
    padding: 4px;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
}

.participants-table,
.race-order-table {
    border-collapse: collapse;
    font-size: 10px;
    width: 100%;
}

.print-staging-compact .participants-table {
    font-size: 10px;
}

.print-staging-compact .participants-table th,
.print-staging-compact .participants-table td {
    padding: 2px 4px;
}

.print-staging-compact .class-name {
    background-color: #eeff00 !important;
    font-size: 14px;
    margin-bottom: 5px;
    padding: 3px;
}

.participants-table th,
.participants-table td,
.race-order-table th,
.race-order-table td {
    border-bottom: 1px solid #d8dde6;
    padding: 3px 4px;
    text-align: left;
    vertical-align: top;
}

.participants-table th,
.race-order-table th {
    background: #edf0f5;
    font-weight: 800;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
}

.race-order-table {
    font-size: 13px;
}

.class-name-line {
    display: block;
}

.race-order-table th:first-child,
.race-order-table td:first-child {
    text-align: center;
    width: 90px;
}

.empty-print-message {
    color: #6b7280;
    font-size: 14px;
    padding: 24px;
    text-align: center;
}

@media print {
    @page {
        margin: 8mm;
    }

    .start-grid-print-page {
        background: #ffffff;
    }

    .print-controls {
        display: none !important;
    }

    .print-container {
        margin: 0;
        max-width: none;
        min-height: 0;
        padding: 0;
    }

    .print-staging-compact .sheet-grid {
        column-count: 3;
        column-fill: auto;
        display: block;
    }

    .print-staging-compact .class-section {
        break-inside: auto;
        page-break-inside: auto;
    }

    .empty-print-message {
        display: none;
    }
}

@media (max-width: 760px) {
    .controls-row {
        align-items: stretch;
        flex-direction: column;
    }

    .actions {
        justify-content: flex-start;
    }

    .sheet-grid {
        column-count: 1;
    }
}
</style>
